import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import api from '../../services/api';
import { jwtDecode } from 'jwt-decode'; // Correct import syntax

const ChangePassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get current user from JWT token
  const getCurrentUser = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return null;
    }
    const decoded = jwtDecode(token); // Now using jwtDecode instead of jwt_decode
    return {
      userName: decoded.sub, // or appropriate claim
      userId: decoded.nameid // or appropriate claim
    };
  };

  const currentUser = getCurrentUser();

  const validate = () => {
    const newErrors = {};
    if (!formData.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Must include uppercase, lowercase, number, and special character';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    
    if (!validate()) return;
  
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        CurrentPassword: formData.currentPassword,
        NewPassword: formData.newPassword,
        UserName: currentUser.userName // Add if your backend requires it
      });
  
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      // Enhanced error handling
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.title || // For ModelState errors
                         'Password change failed';
      setApiError(errorMessage);
      
      // Handle validation errors
      if (error.response?.data?.errors) {
        setErrors(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(error.response.data.errors)
              .map(([key, val]) => [key, val.join(', ')])
          )
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) return null; // Already redirected to login if no token

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 500 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
          Change Your Password
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Logged in as: <strong>{currentUser.userName}</strong>
        </Typography>

        {apiError && <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Password changed successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            type="password"
            label="New Password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword || "At least 8 chars with uppercase, lowercase, number, and special char"}
            margin="normal"
            required
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ px: 4 }}
            >
              {loading ? 'Processing...' : 'Change Password'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ChangePassword;